import React, { Fragment } from "react";

import { Trash2 as BinIcon } from "react-feather";
import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Theme,
  makeStyles,
} from "@material-ui/core";

import { default as BaseModal } from "@ds-developer/modal";
import { CancelLessonModalProps } from "./types";
import {
  Controller,
  useFormContext,
} from "react-hook-form/dist/react-hook-form.ie11";
import Button from "@ds-developer/button";

const useStyles = makeStyles<Theme, { disableRefundToggle: boolean }>(
  theme => ({
    formContainer: {
      paddingTop: theme.spacing(2),
    },
    cancellationReasonText: {
      color: "#263238",
      ["&::placeholder"]: {
        color: "#263238",
      },
    },
    refundText: {
      opacity: ({ disableRefundToggle }) => (disableRefundToggle ? 0.4 : 1),
    },
    refundWrapper: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      marginTop: theme.spacing(1),
      marginLeft: 0,
    },
  }),
);

const CancelLessonModal: React.FC<CancelLessonModalProps> = ({
  handleCancel = () => {},
  open = true,
  handleSubmit = () => {},
  disableRefundToggle = false,
  loading = false,
  submitting,
  handleOnClickTextField,
  handleOnClickRefundRadio,
}) => {
  const {
    cancellationReasonText,
    formContainer,
    refundText,
    refundWrapper,
  } = useStyles({
    disableRefundToggle,
  });

  const {
    register,
    errors,
    formState: { isValid },
    control,
    watch,
  } = useFormContext();

  const { refundLearner } = watch();

  const actions = (
    <Fragment>
      <Button disabled={submitting} onClick={handleCancel} variant="outlined">
        Cancel
      </Button>
      <Button
        onClick={handleSubmit}
        type="submit"
        variant="contained"
        color="error"
        startIcon={<BinIcon size={16} />}
        disabled={!isValid || loading || submitting}
        loading={loading}
        data-test="cancel-lesson-modal-submit"
      >
        Yes, Cancel
      </Button>
    </Fragment>
  );

  return (
    <BaseModal
      actions={actions}
      open={open}
      title="Are you sure you want to cancel this lesson?"
    >
      <Box className={formContainer}>
        <form onSubmit={handleSubmit}>
          <TextField
            multiline
            rows={4}
            placeholder="Please explain why you want to cancel this lesson"
            label="Reason for cancellation"
            inputRef={register}
            fullWidth
            variant="outlined"
            type="text"
            error={Boolean(errors?.reason || !isValid)}
            helperText={errors?.reason?.message}
            name="reason"
            id="reason"
            InputLabelProps={{
              className: cancellationReasonText,
              shrink: true,
            }}
            inputProps={{
              ["data-test"]: "cancel-lesson-modal-input",
              className: cancellationReasonText,
            }}
            onClick={handleOnClickTextField}
          />

          <FormControlLabel
            value={refundLearner}
            className={refundWrapper}
            id="refundLearner"
            onClick={e => {
              handleOnClickRefundRadio((e.target as HTMLInputElement).checked);
            }}
            control={
              <Controller
                as={
                  <Switch
                    name="refundLearner"
                    id="refundLearner"
                    disabled={disableRefundToggle}
                    inputProps={{
                      disabled: disableRefundToggle,
                      "aria-label": "Process refund checkbox",
                    }}
                  />
                }
                name="refundLearner"
                control={control}
              />
            }
            label={
              <span className={refundText}>Refund this lesson to learner</span>
            }
            name="refundLearner"
            labelPlacement="start"
          />
        </form>
      </Box>
    </BaseModal>
  );
};

export default CancelLessonModal;
